import { CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, OnDestroy, effect, input, output, signal, viewChild } from '@angular/core';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-button';
import { FileUploadComponent } from '../aems-file-upload/file-upload.component';
import { ListRepairPartItemsComponent } from '../../components/view-work-order/list-repair-part-items/list-repair-part-items/list-repair-part-items.component';
import { MaintenanceOrderStatusCode } from '../../models/maintenance-order-status.model';
import {
  FilterableRepairLineItem,
  RepairLineItemFilterKey,
  RepairLineItemsFilter,
  repairLineItemFilterFields,
} from './repair-line-items-filter';

type FilterPosition = { left: number; width: number };
export type RepairLineItemsTableElement = HTMLElement & { updateComplete?: Promise<unknown> };

// Repair line item table with per-column filters, expand/collapse all, nested parts and attachments.
// Screen specific columns are rendered by projecting slotted content into the table.
@Component({
  selector: 'app-repair-line-items-table',
  imports: [FileUploadComponent, ListRepairPartItemsComponent],
  templateUrl: './repair-line-items-table.component.html',
  styleUrl: './repair-line-items-table.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairLineItemsTableComponent implements OnDestroy {
  filter = input.required<RepairLineItemsFilter<FilterableRepairLineItem>>();
  columns = input.required<Array<TableColumn>>();
  maintainceOrderStatus = input<MaintenanceOrderStatusCode>();
  maintenanceOrderSourceApplication = input<string | undefined>('');
  workOrderMode = input<string>('');
  sortChange = output<Event>();
  filterBar = viewChild<ElementRef<HTMLElement>>('filterBar');
  repairTable = viewChild<ElementRef<RepairLineItemsTableElement>>('repairTable');
  filterFields = repairLineItemFilterFields;
  filterPositions = signal<Partial<Record<RepairLineItemFilterKey, FilterPosition>>>({});
  private headerResizeObserver?: ResizeObserver;

  constructor() {
    // Column widths are content driven, so re-align the filters whenever the visible rows change
    effect(() => {
      this.filter().filteredData();
      const table = this.repairTable()?.nativeElement;
      if (table) void this.alignFiltersToHeaders(table);
    });
  }

  ngOnDestroy(): void {
    this.headerResizeObserver?.disconnect();
  }

  onFilterChange(key: RepairLineItemFilterKey, event: Event) {
    this.filter().setFilter(key, (event.target as HTMLInputElement).value);
  }

  async alignFiltersToHeaders(table: RepairLineItemsTableElement) {
    if (typeof ResizeObserver === 'undefined') return;
    await table.updateComplete;
    const headers = this.filterFields.map((field) => findHeader(table, field.id)).filter((th): th is HTMLElement => !!th);
    if (headers.length === 0) return;

    if (!this.headerResizeObserver) {
      this.headerResizeObserver = new ResizeObserver(() => this.measureFilterPositions(table));
      this.headerResizeObserver.observe(table);
    }
    const observer = this.headerResizeObserver;
    headers.forEach((th) => observer.observe(th));
    this.measureFilterPositions(table);
  }

  measureFilterPositions(table: RepairLineItemsTableElement) {
    const bar = this.filterBar()?.nativeElement;
    if (!bar) return;
    const barLeft = bar.getBoundingClientRect().left;
    const positions: Partial<Record<RepairLineItemFilterKey, FilterPosition>> = {};
    for (const field of this.filterFields) {
      const th = findHeader(table, field.id);
      if (!th) continue;
      const rect = th.getBoundingClientRect();
      const style = getComputedStyle(th);
      const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(style.paddingRight) || 0;
      positions[field.id] = {
        left: rect.left - barLeft + paddingLeft,
        width: Math.max(rect.width - paddingLeft - paddingRight, 0),
      };
    }
    this.filterPositions.set(positions);
  }
}

// mc-table renders its headers in a shadow root, keyed by data-header-id
function findHeader(table: HTMLElement, id: RepairLineItemFilterKey) {
  return table.shadowRoot?.querySelector<HTMLElement>(`th[data-header-id="${id}"]`) ?? null;
}
